import { Schema } from "effect";
import { GmailProviderData } from "./gmail";
import { OutlookProviderData } from "./outlook";

// Union of all email provider data types
export const EmailProviderData = Schema.Union(
  GmailProviderData,
  OutlookProviderData,
);
export type EmailProviderData = typeof EmailProviderData.Type;

export const MessageEmailAddressType = Schema.Literal(
  "from",
  "to",
  "cc",
  "bcc",
  // The Reply-To address, stored alongside the true "from" when an inbound
  // message was relayed through a no-reply sender (e.g. a Shopify contact
  // form). The UI shows "from" as the real sender but defaults replies and
  // customer attribution to "replyTo". See selectReplyTargetAddress.
  "replyTo",
);
export type MessageEmailAddressType = typeof MessageEmailAddressType.Type;

export const EmailAddressEntry = Schema.Struct({
  addressType: MessageEmailAddressType,
  email: Schema.String.pipe(Schema.minLength(1)),
  name: Schema.NullOr(Schema.String),
});
export type EmailAddressEntry = typeof EmailAddressEntry.Type;

/**
 * Pick the address an inbound email should be replied to and attributed to.
 *
 * Prefers an explicit `replyTo` entry (present when the message was relayed
 * through a no-reply `from`, e.g. a Shopify contact form) over the true `from`
 * sender. Used by reply-composer seeding, customer attribution, and survey
 * targeting so they all agree on the customer's real address while the `from`
 * entry continues to drive the displayed sender.
 *
 * Generic over any address shape so both the shared `EmailAddressEntry` (UI,
 * events) and the persisted `MessageEmailAddress` rows (services, where `name`
 * is an Option) can use it.
 */
export function selectReplyTargetAddress<
  T extends { readonly addressType: string },
>(addresses: readonly T[]): T | undefined {
  return (
    addresses.find((address) => address.addressType === "replyTo") ??
    addresses.find((address) => address.addressType === "from")
  );
}

import { type EmailChannel, isEmailChannel } from "../channel-utils";
import type { Message } from "../message";

export type EmailMessage = Extract<Message, { channel: EmailChannel }>;

export const isEmailMessage = (message: Message): message is EmailMessage =>
  isEmailChannel(message.channel);
